import { toast } from 'sonner';
import { isNativeApp, getPlatform } from './platform';
import {
  AppUpdate,
  AppUpdateAvailability,
  FlexibleUpdateInstallStatus,
} from '@capawesome/capacitor-app-update';

// Flexible in-app update flow.
// Android: uses Google Play In-App Updates — downloads in the background, then
//          prompts the user to restart to apply.
// iOS: no native in-app update API exists, so we check the App Store version and
//      prompt the user to open the store listing to update.
export async function checkForAppUpdate() {
  if (!isNativeApp()) return;

  let info;
  try {
    info = await AppUpdate.getAppUpdateInfo();
  } catch {
    // App not yet live on the store, or store info unavailable — skip silently.
    return;
  }

  if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) return;

  const platform = getPlatform();

  if (platform === 'android') {
    if (info.flexibleUpdateAllowed) {
      await startAndroidFlexibleUpdate();
    } else if (info.immediateUpdateAllowed) {
      try { await AppUpdate.performImmediateUpdate(); } catch { /* user dismissed */ }
    } else {
      try { await AppUpdate.openAppStore(); } catch { /* ignore */ }
    }
    return;
  }

  // iOS
  toast('Update available', {
    description: `Version ${info.availableVersionName || ''} is ready in the App Store.`.trim(),
    duration: Infinity,
    action: {
      label: 'Update',
      onClick: () => { AppUpdate.openAppStore().catch(() => {}); },
    },
  });
}

async function startAndroidFlexibleUpdate() {
  let handle;
  try {
    handle = await AppUpdate.addListener('onFlexibleUpdateStateChange', (state) => {
      if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
        toast('Update downloaded', {
          description: 'Restart to finish updating MoneyFeed.',
          duration: Infinity,
          action: {
            label: 'Restart',
            onClick: () => { AppUpdate.completeFlexibleUpdate().catch(() => {}); },
          },
        });
      }
    });
    await AppUpdate.startFlexibleUpdate();
  } catch {
    // User cancelled the update consent dialog — nothing to do.
    if (handle) { try { await handle.remove(); } catch { /* ignore */ } }
  }
}
